<template>
  <div class="cart-sidebar-overlay" v-show="isOpen" @click="closeSidebar">
    <div class="cart-sidebar" :class="{ 'open': isOpen }" @click.stop>
      <!-- Header -->
      <div class="cart-sidebar-header">
        <h5 class="cart-sidebar-title">
          <i class="bi bi-basket-fill me-2"></i>
          Καλάθι Αγορών
          <span class="item-count" v-if="cartStore.itemCount > 0">({{ cartStore.itemCount }})</span>
        </h5>
        <button class="btn-close-sidebar" @click="closeSidebar">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="cart-sidebar-content">
        <!-- Empty Cart -->
        <div v-if="cartStore.isEmpty" class="empty-cart-sidebar">
          <div class="empty-cart-icon">
            <i class="bi bi-cart-x"></i>
          </div>
          <h6>Το καλάθι σας είναι άδειο</h6>
          <p>Προσθέστε προϊόντα για να συνεχίσετε</p>
          <router-link to="/shop" class="btn btn-primary btn-sm" @click="closeSidebar">
            <i class="bi bi-shop me-1"></i>Περιηγηθείτε στα Προϊόντα
          </router-link>
        </div>

        <!-- Cart Items -->
        <div v-else class="cart-items-sidebar">
          <div class="cart-item-sidebar" v-for="item in cartStore.items" :key="item.id">
            <div class="item-image">
              <img 
                :src="getImageUrl(item.image)" 
                :alt="item.name"
                @error="handleImageError"
              >
            </div>
            
            <div class="item-details">
              <h6 class="item-name">{{ item.name }}</h6>
              <div class="item-price">€{{ item.price.toFixed(2) }}</div>
              
              <div class="item-controls">
                <div class="quantity-controls">
                  <button 
                    class="btn-quantity"
                    @click="decreaseQuantity(item)"
                    :disabled="item.quantity <= 1"
                  >
                    <i class="bi bi-dash"></i>
                  </button>
                  <span class="quantity">{{ item.quantity }}</span>
                  <button 
                    class="btn-quantity"
                    @click="increaseQuantity(item)"
                  >
                    <i class="bi bi-plus"></i>
                  </button>
                </div>
                
                <button 
                  class="btn-remove"
                  @click="removeFromCart(item)"
                  title="Αφαίρεση από καλάθι"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
              
              <div class="item-total">Σύνολο: €{{ item.total.toFixed(2) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="cart-sidebar-footer" v-if="!cartStore.isEmpty">
        <div class="cart-summary">
          <div class="summary-row">
            <span>Υποσύνολο:</span>
            <span class="amount">€{{ cartStore.totalAmount.toFixed(2) }}</span>
          </div>
          <div class="summary-row total">
            <span>Σύνολο:</span>
            <span class="amount">€{{ finalTotal.toFixed(2) }}</span>
          </div>
        </div>
        
        <div class="cart-actions">
          <router-link 
            to="/cart" 
            class="btn btn-outline-primary btn-sm me-2"
            @click="closeSidebar"
          >
            <i class="bi bi-eye me-1"></i>Προβολή Καλαθιού
          </router-link>
          
          <router-link 
            to="/checkout" 
            class="btn btn-success btn-sm"
            @click="closeSidebar"
          >
            <i class="bi bi-credit-card me-1"></i>Ταμείο
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { useCartStore } from '@/stores/cart'
import { getImageUrl as configGetImageUrl } from '@/config/env'

interface Props {
  isOpen: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
}>()

const cartStore = useCartStore()

// Computed properties
const finalTotal = computed(() => {
  const shippingCost = cartStore.totalAmount >= 50 ? 0 : 5.99
  return cartStore.totalAmount + shippingCost
})

// Methods
const closeSidebar = () => {
  emit('close')
}

// Handle ESC key
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.isOpen) {
    closeSidebar()
  }
}

// Add/remove event listeners
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

const getImageUrl = (imagePath: string) => {
  if (!imagePath || imagePath === '/placeholder-product.jpg') {
    return configGetImageUrl('/assets/placeholder-product.jpg')
  }
  // Handle both products and gallery images
  if (imagePath.startsWith('/products/')) {
    return configGetImageUrl(imagePath)
  } else if (imagePath.startsWith('/gallery/')) {
    return configGetImageUrl(imagePath)
  } else {
    // Assume it's a product image if no prefix
    return configGetImageUrl(`/products/${imagePath}`)
  }
}

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  target.src = configGetImageUrl('/assets/placeholder-product.jpg')
}

const increaseQuantity = (item: any) => {
  cartStore.updateQuantity(item.product_id, item.quantity + 1)
}

const decreaseQuantity = (item: any) => {
  if (item.quantity > 1) {
    cartStore.updateQuantity(item.product_id, item.quantity - 1)
  }
}

const removeFromCart = (item: any) => {
  if (confirm(`Αφαίρεση "${item.name}" από το καλάθι;`)) {
    cartStore.removeFromCart(item.product_id)
  }
}
</script>

<style scoped>
.cart-sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
}

.cart-sidebar {
  position: fixed;
  top: 0;
  right: -400px;
  width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.1);
  transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
}

.cart-sidebar.open {
  right: 0;
}

/* Header */
.cart-sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  color: white;
  border-bottom: 1px solid #e9ecef;
}

.cart-sidebar-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.item-count {
  font-size: 0.9rem;
  opacity: 0.9;
}

.btn-close-sidebar {
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.btn-close-sidebar:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Content */
.cart-sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

/* Empty Cart */
.empty-cart-sidebar {
  text-align: center;
  padding: 3rem 2rem;
  color: #6c757d;
}

.empty-cart-icon {
  font-size: 3rem;
  color: #dee2e6;
  margin-bottom: 1rem;
}

.empty-cart-sidebar h6 {
  margin-bottom: 0.5rem;
  color: #495057;
}

.empty-cart-sidebar p {
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
}

/* Cart Items */
.cart-items-sidebar {
  padding: 0;
}

.cart-item-sidebar {
  display: flex;
  padding: 1.5rem;
  border-bottom: 1px solid #f8f9fa;
  transition: background-color 0.2s ease;
}

.cart-item-sidebar:hover {
  background: #f8f9fa;
}

.cart-item-sidebar:last-child {
  border-bottom: none;
}

.item-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  margin-right: 1rem;
  flex-shrink: 0;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-details {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  line-height: 1.3;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-price {
  font-size: 0.9rem;
  color: #e74c3c;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.item-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.quantity-controls {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 2px;
}

.btn-quantity {
  background: none;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: #6c757d;
  transition: all 0.2s ease;
  cursor: pointer;
}

.btn-quantity:hover:not(:disabled) {
  background: #e9ecef;
  color: #495057;
}

.btn-quantity:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity {
  min-width: 30px;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
}

.btn-remove {
  background: none;
  border: none;
  color: #dc3545;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.btn-remove:hover {
  background: rgba(220, 53, 69, 0.1);
}

.item-total {
  font-size: 0.85rem;
  font-weight: 600;
  color: #28a745;
}

/* Footer */
.cart-sidebar-footer {
  border-top: 1px solid #e9ecef;
  padding: 1.5rem;
  background: #f8f9fa;
}

.cart-summary {
  margin-bottom: 1rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.summary-row.total {
  font-weight: 600;
  font-size: 1rem;
  color: #2c3e50;
  border-top: 1px solid #dee2e6;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
}

.amount {
  font-weight: 600;
  color: #e74c3c;
}

.cart-actions {
  display: flex;
  gap: 0.5rem;
}

.cart-actions .btn {
  flex: 1;
  font-size: 0.85rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-weight: 600;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .cart-sidebar {
    width: 100vw;
    right: -100vw;
  }
  
  .cart-sidebar.open {
    right: 0;
  }
}

@media (max-width: 480px) {
  .cart-sidebar-header {
    padding: 1rem;
  }
  
  .cart-sidebar-title {
    font-size: 1rem;
  }
  
  .cart-item-sidebar {
    padding: 1rem;
  }
  
  .item-image {
    width: 50px;
    height: 50px;
  }
  
  .cart-sidebar-footer {
    padding: 1rem;
  }
}

/* Scroll styling */
.cart-sidebar-content::-webkit-scrollbar {
  width: 6px;
}

.cart-sidebar-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.cart-sidebar-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.cart-sidebar-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
